import React, { createContext, useContext, useState, useEffect } from 'react';
import authAPI from '../services/authAPI';
import googleAuthService from '../services/googleAuth';
import { setTokens, getTokens, removeTokens } from '../utils/tokenStorage';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing auth token and validate it
    const { access } = getTokens();
    if (access) {
      // Validate token by fetching user data
      authAPI.getCurrentUser()
      .then(userData => {
        setUser(userData);
      })
      .catch(error => {
        // Token is invalid, remove it
        removeTokens();
      })
      .finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);


  const login = async (email, password) => {
    try {
      const data = await authAPI.login(email, password);
      setTokens(data.access, data.refresh);
      
      // Fetch user data using authAPI
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      return { success: true };
    } catch (error) {
      const status = error.response?.status;
      if (status === 401) return { success: false, error: 'Email or password is incorrect.' };
      if (status === 429) return { success: false, error: 'Too many sign-in attempts. Please wait a minute and try again.' };
      return { success: false, error: error.response?.data?.detail || error.message || 'Login failed' };
    }
  };

  const register = async (userData) => {
    try {
      const data = await authAPI.register(userData);
      return { success: true, message: data.message };
    } catch (error) {
      console.error('Registration error:', error);
      // Extract error message from response
      const errorData = error.response?.data;
      let errorMessage = 'Registration failed';
      
      if (errorData) {
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else {
          // Return the full error object for field-specific errors
          return { success: false, error: errorData };
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    removeTokens();
    setUser(null);
  };

  const googleLogin = async () => {
    try {
      // Initialize Google Auth service if needed
      await googleAuthService.initialize();
      
      // Redirect to Google OAuth
      googleAuthService.redirectToGoogle();
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message || 'Google login failed' };
    }
  };

  // Google One Tap / Sign In With Google: exchange the ID token (credential) for our JWTs
  const loginWithGoogleCredential = async (credential) => {
    try {
      const data = await authAPI.googleOneTap(credential);
      setTokens(data.access, data.refresh);
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      return { success: true, created: data.created };
    } catch (error) {
      return { success: false, error: error.response?.data?.error || 'Google sign-in failed' };
    }
  };

  const handleGoogleCallback = async () => {
    try {
      const authData = await googleAuthService.completeOAuthFlow();
      
      // Store tokens
      setTokens(authData.access, authData.refresh);
      
      // Set user data
      setUser(authData.user);
      
      return { success: true, user: authData.user, created: authData.created };
    } catch (error) {
      return { success: false, error: error.message || 'Google authentication failed' };
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    googleLogin,
    loginWithGoogleCredential,
    handleGoogleCallback,
    loading,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
